import React, { FunctionComponent, useState } from 'react';
import { Button, Spinner } from '../ui';
import { Link, useLocation } from 'react-router-dom';
import slackInfoSvg from '../../assets/images/slack-info.svg';
import slackLogoSvg from '../../assets/images/slack-logo.svg';
import { useAvailableIntegration } from '../../hooks/useAvailableIntegration';
import { useRefreshBriefDetails } from '../../hooks/useBriefs';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { AppType } from '../../services/models/app-type';
import { Brief } from '../../services/models/brief';
import { FeatureFlag } from '../../services/models/feature-flags';
import { SlackChannel } from '../../services/models/slack-channel';
import { launchPopupWindow } from '../../services/popups';
import { getAddToSlackLink } from '../../services/slack';
import DisconnectSlackChannelDialog from './components/disconnect-slack-channel-dialog/DisconnectSlackChannelDialog';

interface Props {
  brief: Brief;
  destroy: (id: number) => void;
}

const EditSlackIntegration: FunctionComponent<Props> = ({ brief, destroy }) => {
  const refreshBriefDetails = useRefreshBriefDetails();
  const location = useLocation();

  const [selectedSlackChannel, setSelectedSlackChannel] = useState<SlackChannel | null>(null);

  const slackFeatureFlag = useFeatureFlag(FeatureFlag.ShowAddToSlack);
  const isSlackIntegrationAvailable = useAvailableIntegration(AppType.Slack);

  if (!slackFeatureFlag) {
    return <></>;
  }

  const isLoading = () => {
    return brief.isUpdatingSlackChannels;
  };

  const addSlackChannel = async () => {
    if (isLoading()) {
      return;
    }

    const addToSlackUrl = await getAddToSlackLink(brief.id, location.pathname);

    if (addToSlackUrl) {
      launchPopupWindow(
        addToSlackUrl,
        'location=yes,scrollbars=yes,status=yes',
        { height: 700, width: 800 },
        () => {
          refreshBriefDetails(brief.id);
        }
      );
    }
  };

  const removeSelectedSlackChannel = () => {
    if (!isLoading() && selectedSlackChannel) {
      destroy(selectedSlackChannel.id);
      setSelectedSlackChannel(null);
    }
  };

  const getSlackChannels = () => {
    const list = brief?.slackChannels || [];

    if (list.length === 0) {
      return null;
    }

    return (
      <div className="mt-5 flex w-full flex-col overflow-hidden rounded-md border border-gray-400">
        <div className="h-[45px] bg-primary px-2.5 text-left font-['Ubuntu'] text-[15px] font-bold tracking-[0.5px] leading-[45px] text-white">
          Slack channels connected
        </div>
        {list.map((x, i) => (
          <div
            className="group flex h-[45px] items-center justify-between border-t border-gray-300 bg-gray-100 px-2.5 text-primary"
            key={i}
          >
            <span>{x.name}</span>
            <Button
              variant="tool-button"
              className="transition-opacity pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100"
              onClick={() => destroy(x.id)}
            >
              Delete
            </Button>
          </div>
        ))}
      </div>
    );
  };

  const getSlackButton = () => {
    return isSlackIntegrationAvailable ? (
      <Button
        onClick={() => addSlackChannel()}
        variant="white"
        className="flex flex-row items-center"
        disabled={isLoading()}
      >
        <img src={slackLogoSvg} alt="" className="mr-2 h-6 w-6" />
        <span className="mr-2">
          Add to <strong> Slack</strong>
        </span>
        {(() => {
          if (isLoading()) {
            return <Spinner size="sm" animation="border"></Spinner>;
          }
        })()}
      </Button>
    ) : (
      <Button variant="purple" as={Link} to="/dashboard/pricing">
        Upgrade Plan
      </Button>
    );
  };

  return (
    <div className="mx-auto mb-[30px] flex max-w-[260px] flex-col items-start justify-center">
      <div className="font-['Ubuntu'] text-[18px] font-bold tracking-[0.5px] text-black">
        Slack channels
      </div>

      <div className="font-['Ubuntu'] text-[18px] font-light tracking-[0.5px] text-gray-700">
        Add or Remove the channels your News Brief is connected to.
      </div>

      <div className="mt-5 mb-5 flex w-full flex-col items-center justify-center">
        <img className="mx-auto my-[50px] block" src={slackInfoSvg} alt="Slack Info" />
        <div className="flex flex-row">{getSlackButton()}</div>
        <div className="intelligence-edit-report-form-tags-container js-keyword-tag-container">
          {getSlackChannels()}
        </div>
      </div>

      <DisconnectSlackChannelDialog
        slackChannel={selectedSlackChannel}
        onAccept={() => removeSelectedSlackChannel()}
        onCancel={() => setSelectedSlackChannel(null)}
      />
    </div>
  );
};

export default EditSlackIntegration;
